import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { ORIGINAL_LANGUAGE_SIGNIFIER } from "../components/shared/TranslationDropDown";
import { useTranslationConfig } from "./useTranslationConfig";

interface Language {
  language: string;
  name: string;
  supports_formality: boolean;
}

export function useTranslationLanguages() {
  const currentMember = Preload.current_member;
  const { isTranslationEnabled } = useTranslationConfig();

  const { data: languages } = useQuery<Language[]>("/translations/languages", {
    refetchOnWindowFocus: false,
    enabled: isTranslationEnabled,
  });

  const [selectedLanguage, setSelectedLanguageState] = useState(
    ORIGINAL_LANGUAGE_SIGNIFIER,
  );

  const setSelectedLanguage = (language: string) => {
    setSelectedLanguageState(language.toUpperCase());
  };

  const categorizedLanguages = useMemo(() => {
    if (!languages) return { primary: [], secondary: [] };

    const primaryLanguageKeys = [
      selectedLanguage !== ORIGINAL_LANGUAGE_SIGNIFIER
        ? selectedLanguage
        : currentMember?.last_used_translation_language,
      "DE",
      "EN-GB",
      "ES",
      "FR",
    ];

    const primary = languages
      .filter((lang) => primaryLanguageKeys.includes(lang.language))
      .sort(
        (a, b) =>
          primaryLanguageKeys.indexOf(a.language) -
          primaryLanguageKeys.indexOf(b.language),
      );

    const secondary = languages.filter(
      (lang) => !primaryLanguageKeys.includes(lang.language),
    );

    return { primary, secondary };
  }, [languages, selectedLanguage, currentMember]);

  return {
    primaryLanguages: categorizedLanguages.primary,
    secondaryLanguages: categorizedLanguages.secondary,
    selectedLanguage,
    setSelectedLanguage,
  };
}
